"use client";
// Carrusel de opiniones de Google: tarjetas que se deslizan (con el dedo, las flechas o los puntos), con «Leer más» en las largas
// y el logotipo de Google en cada una, como lo pide Google al mostrar sus opiniones. Los datos llegan del servidor tal cual los da Google.
import { useCallback, useEffect, useRef, useState } from "react";
import type { Opinion } from "@/lib/resenas-google";
import { Img } from "@/components/Img";

const COLORES = ["#2f8a57", "#d2622d", "#2fa9df", "#7b5cd6", "#139c96", "#ef5b8c", "#a8481c", "#5f6956"];
const color = (nombre: string) => COLORES[[...nombre].reduce((s, c) => s + c.charCodeAt(0), 0) % COLORES.length];

function LogoGoogle() {
  return <svg className="oc-google" viewBox="0 0 48 48" width="24" height="24" role="img" aria-label="Google">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>;
}

function Tarjeta({ o, es }: { o: Opinion; es: boolean }) {
  const texto = useRef<HTMLParagraphElement>(null);
  const [larga, setLarga] = useState(false), [abierta, setAbierta] = useState(false);
  useEffect(() => { const p = texto.current; if (p) setLarga(p.scrollHeight > p.clientHeight + 2); }, [o.texto]);
  const n = Math.round(o.estrellas);
  return <li className="oc-tarjeta">
    <header>
      {o.foto ? <Img src={o.foto} alt="" width={44} height={44} loading="lazy" referrerPolicy="no-referrer" />
        : <span className="oc-inicial" style={{ background: color(o.autor) }} aria-hidden="true">{o.autor.charAt(0).toUpperCase()}</span>}
      <div><b>{o.autorUrl ? <a href={o.autorUrl} target="_blank" rel="noopener nofollow">{o.autor}</a> : o.autor}</b><small>{o.cuando}</small></div>
    </header>
    <p ref={texto} className={abierta ? "abierta" : ""}>{o.texto}</p>
    {(larga || abierta) && <button type="button" className="oc-mas" aria-expanded={abierta} onClick={() => setAbierta((x) => !x)}>{abierta ? (es ? "Ver menos" : "Show less") : (es ? "Leer más" : "Read more")}</button>}
    <footer>
      <span className="oc-estrellas" role="img" aria-label={es ? `${n} de 5 estrellas` : `${n} out of 5 stars`}>{"★".repeat(n)}<i>{"★".repeat(5 - n)}</i></span>
      <LogoGoogle />
    </footer>
  </li>;
}

export function CarruselOpiniones({ opiniones, es, escribir, verTodas }: { opiniones: Opinion[]; es: boolean; escribir: string | null; verTodas: string }) {
  const pista = useRef<HTMLUListElement>(null);
  const [actual, setActual] = useState(0), [bordes, setBordes] = useState({ inicio: true, fin: false });

  const medir = useCallback(() => {
    const el = pista.current; if (!el) return;
    const paso = (el.firstElementChild as HTMLElement | null)?.offsetWidth ?? el.clientWidth;
    const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
    setActual(Math.round(el.scrollLeft / (paso + gap)));
    setBordes({ inicio: el.scrollLeft < 4, fin: el.scrollLeft + el.clientWidth >= el.scrollWidth - 4 });
  }, []);
  useEffect(() => { medir(); window.addEventListener("resize", medir); return () => window.removeEventListener("resize", medir); }, [medir]);

  const ir = (i: number) => {
    const el = pista.current, hijo = el?.children[Math.max(0, Math.min(opiniones.length - 1, i))] as HTMLElement | undefined;
    if (el && hijo) el.scrollTo({ left: hijo.offsetLeft - el.offsetLeft, behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  };

  return <div className="oc" role="region" aria-roledescription={es ? "carrusel" : "carousel"} aria-label={es ? "Opiniones de nuestros visitantes" : "Reviews from our visitors"}>
    <div className="oc-marco">
      <button type="button" className="oc-flecha atras" onClick={() => ir(actual - 1)} disabled={bordes.inicio} aria-label={es ? "Opinión anterior" : "Previous review"}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
      </button>
      <ul ref={pista} className="oc-pista" onScroll={medir} tabIndex={0} aria-label={es ? "Desliza para ver más opiniones" : "Swipe to see more reviews"}>
        {opiniones.map((o) => <Tarjeta key={o.autor + o.texto.slice(0, 30)} o={o} es={es} />)}
      </ul>
      <button type="button" className="oc-flecha adelante" onClick={() => ir(actual + 1)} disabled={bordes.fin} aria-label={es ? "Siguiente opinión" : "Next review"}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
      </button>
    </div>
    {opiniones.length > 1 && <div className="oc-puntos">
      {opiniones.map((o, i) => <button key={i} type="button" aria-label={es ? `Ir a la opinión ${i + 1} de ${opiniones.length}` : `Go to review ${i + 1} of ${opiniones.length}`} aria-current={i === actual ? "true" : undefined} onClick={() => ir(i)} />)}
    </div>}
    <div className="oc-acciones">
      {escribir && <a className="oc-escribir" href={escribir} target="_blank" rel="noopener"><LogoGoogle />{es ? "Escribir una reseña en Google" : "Write a review on Google"}</a>}
      <a className="oc-todas" href={verTodas} target="_blank" rel="noopener">{es ? "Ver todas en Google" : "See all on Google"}</a>
    </div>
  </div>;
}
